import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { Globe, Plus, Trash2 } from "lucide-react"
import { Button } from "@ui/Button"
import { Card } from "@ui/Card"
import { Checkbox } from "@ui/Checkbox"
import { Dialog } from "@ui/Dialog"
import { EmptyState } from "@ui/EmptyState"
import { Field } from "@ui/Field"
import { Input } from "@ui/Input"
import { Select } from "@ui/Select"
import type { Environment, EnvironmentVariable, Project } from "@lib/inertia-types"

interface EnvWithVars extends Environment {
  variables: EnvironmentVariable[]
}

interface Service {
  id: number
  name: string
  slug: string
}

export default function EnvironmentsIndex({
  project,
  services,
  environments,
}: {
  project: Project
  services: Service[]
  environments: EnvWithVars[]
}) {
  const [newEnvOpen, setNewEnvOpen] = React.useState(false)

  const projectWide = environments.filter((e) => e.folder_id === null)
  const byService = services.map((s) => ({
    service: s,
    envs: environments.filter((e) => e.folder_id === s.id),
  }))

  return (
    <>
      <Head title={`Environments · ${project.name} · Fetch`} />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-title3 font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
              Environments
            </h1>
            <p className="mt-1 text-small text-cloverhound-gray-600">
              Each API service defines its own environments (dev, qa, prod…).
              Project-wide sets are shared across all services.
            </p>
          </div>
          <Button onClick={() => setNewEnvOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            New environment
          </Button>
        </div>

        {environments.length === 0 && (
          <EmptyState
            icon={<Globe className="size-6" aria-hidden="true" />}
            title="No environments yet"
            description="Create dev/qa/prod sets per API service, or a project-wide shared set."
          />
        )}

        {byService.map(
          ({ service, envs }) =>
            envs.length > 0 && (
              <EnvSection
                key={service.id}
                title={service.name}
                envs={envs}
                project={project}
              />
            ),
        )}
        {projectWide.length > 0 && (
          <EnvSection title="Project-wide (shared)" envs={projectWide} project={project} />
        )}
      </div>

      <NewEnvironmentDialog
        open={newEnvOpen}
        onClose={() => setNewEnvOpen(false)}
        project={project}
        services={services}
      />
    </>
  )
}

function EnvSection({
  title,
  envs,
  project,
}: {
  title: string
  envs: EnvWithVars[]
  project: Project
}) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-small font-semibold uppercase tracking-wide text-cloverhound-gray-600">
        {title}
      </h2>
      <div className="flex flex-col gap-4">
        {envs.map((env) => (
          <EnvironmentCard key={env.id} env={env} project={project} />
        ))}
      </div>
    </section>
  )
}

function EnvironmentCard({ env, project }: { env: EnvWithVars; project: Project }) {
  const [vars, setVars] = React.useState(env.variables.map((v) => ({ ...v })))
  const [dirty, setDirty] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    setVars(env.variables.map((v) => ({ ...v })))
    setDirty(false)
  }, [env.variables])

  function updateVar(index: number, patch: Partial<EnvironmentVariable>) {
    setVars((prev) => prev.map((v, i) => (i === index ? { ...v, ...patch } : v)))
    setDirty(true)
  }

  function addVar() {
    setVars((prev) => [...prev, { id: 0, key: "", value: "", secret: false }])
    setDirty(true)
  }

  function removeVar(index: number) {
    setVars((prev) => prev.filter((_, i) => i !== index))
    setDirty(true)
  }

  function save() {
    setSaving(true)
    router.put(
      `/projects/${project.slug}/environments/${env.id}/variables`,
      {
        variables: vars
          .filter((v) => v.key.trim())
          .map((v) => ({ key: v.key.trim(), value: v.value ?? "", secret: v.secret })),
      } as unknown as Record<string, never>,
      {
        preserveScroll: true,
        preserveState: true,
        only: ["environments"],
        onFinish: () => {
          setSaving(false)
          setDirty(false)
        },
      },
    )
  }

  function deleteEnv() {
    if (!confirm(`Delete environment "${env.name}" and its variables?`)) return
    router.delete(`/projects/${project.slug}/environments/${env.id}`, {
      preserveScroll: true,
      only: ["environments"],
    })
  }

  return (
    <Card padding="md">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="rounded-sm bg-cloverhound-indigo-50 px-1.5 py-0.5 font-mono text-mini font-semibold text-cloverhound-indigo-700 dark:bg-cloverhound-indigo-900/40 dark:text-cloverhound-indigo-300">
            {env.slug}
          </span>
          <h3 className="text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
            {env.name}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <Button size="xs" onClick={save} loading={saving}>
              Save variables
            </Button>
          )}
          <Button variant="ghost" size="iconXs" onClick={deleteEnv} aria-label="Delete environment">
            <Trash2 className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {vars.length > 0 && (
          <div className="grid grid-cols-[1fr_2fr_60px_28px] gap-2 px-1 text-micro font-semibold uppercase tracking-wide text-cloverhound-gray-500">
            <span>Key</span>
            <span>Value</span>
            <span>Secret</span>
            <span />
          </div>
        )}
        {vars.map((v, i) => (
          <div key={i} className="grid grid-cols-[1fr_2fr_60px_28px] items-center gap-2">
            <Input
              size="sm"
              value={v.key}
              onChange={(e) => updateVar(i, { key: e.target.value })}
              placeholder="base_url"
              className="font-mono"
            />
            <Input
              size="sm"
              type={v.secret ? "password" : "text"}
              value={v.value ?? ""}
              onChange={(e) => updateVar(i, { value: e.target.value })}
              placeholder="value"
              className="font-mono"
            />
            <div className="flex justify-center">
              <Checkbox
                checked={v.secret}
                onCheckedChange={(checked) => updateVar(i, { secret: checked === true })}
                aria-label="Secret"
              />
            </div>
            <Button variant="ghost" size="iconXs" onClick={() => removeVar(i)} aria-label="Remove variable">
              <Trash2 className="size-3.5" aria-hidden="true" />
            </Button>
          </div>
        ))}
        <div>
          <Button variant="ghost" size="xs" onClick={addVar}>
            <Plus className="size-3.5" aria-hidden="true" />
            Add variable
          </Button>
        </div>
      </div>
    </Card>
  )
}

function NewEnvironmentDialog({
  open,
  onClose,
  project,
  services,
}: {
  open: boolean
  onClose: () => void
  project: Project
  services: Service[]
}) {
  const [name, setName] = React.useState("")
  const [scope, setScope] = React.useState<string>("project")
  const [submitting, setSubmitting] = React.useState(false)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    router.post(
      `/projects/${project.slug}/environments`,
      { name, folder_id: scope === "project" ? null : Number(scope) },
      {
        preserveScroll: true,
        only: ["environments"],
        onFinish: () => {
          setSubmitting(false)
          setName("")
          onClose()
        },
      },
    )
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Content size="sm">
        <form onSubmit={submit}>
          <Dialog.Header>
            <Dialog.Title>New environment</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-4">
            <Field label="Name" required>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="dev" autoFocus />
            </Field>
            <Field label="Scope">
              <Select.Root value={scope} onValueChange={(v) => v && setScope(v)}>
                <Select.Trigger aria-label="Scope">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="project">Project-wide (shared)</Select.Item>
                  {services.map((s) => (
                    <Select.Item key={s.id} value={String(s.id)}>
                      {s.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Field>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={!name.trim()}>
              Create
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}
